import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { describeError } from '../../lib/describeError'
import {
  RETRY_LABEL, SOCIAL_CHANNELS, awardLabel, awardRide, defaultPeriod, emailLine, grantLine, magazinePackageHref, prizeYears,
  retryAction, retryGrant,
  type AwardKind, type AwardResult, type RideDetail, type SocialChannel,
} from './ridesApi'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const field = `min-h-11 rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper ${focus}`
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnSm = `inline-flex min-h-8 items-center justify-center rounded-md border border-ink-400 px-2.5 font-ui text-[12px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-40 ${focus}`
const btnPrimary = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-amber-glow bg-amber-glow px-4 font-ui text-[14px] font-semibold text-ink-900 hover:opacity-90 disabled:opacity-50 ${focus}`
const lab = 'flex flex-col gap-1 font-ui text-[13px] font-semibold text-paper-dim'
const toneText = { ok: 'text-radar-cyan', warn: 'text-amber-deep', bad: 'text-radar-red' } as const

/** Ready-to-post text for each channel, each with a Copy button. */
export function SocialCopy({ social }: { social: Record<SocialChannel, string> }) {
  const [copied, setCopied] = useState<SocialChannel | null>(null)
  const copy = (k: SocialChannel) => {
    void navigator.clipboard?.writeText(social[k]).then(() => setCopied(k), () => setCopied(null))
  }
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {SOCIAL_CHANNELS.map((c) => (
        <div key={c.key} className="flex flex-col gap-1.5">
          <span className="font-ui text-[12px] font-bold uppercase tracking-[0.06em] text-paper-faint">{c.label}</span>
          <textarea readOnly aria-label={`${c.label} post`} value={social[c.key]} className={`${field} min-h-28 py-2 text-[13px]`} />
          <button type="button" className={`${btnSm} w-fit`} onClick={() => copy(c.key)}>{copied === c.key ? 'Copied' : `Copy ${c.label} post`}</button>
        </div>
      ))}
    </div>
  )
}

/** The four things an award does, as they turned out. */
export function AwardOutcome({ pub, rideId, result, onRetried }: {
  pub: string; rideId: number; result: AwardResult; onRetried: (r: AwardResult) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const retry = useMutation({
    mutationFn: () => retryGrant(pub, result.award_id),
    onSuccess: (r) => { setError(null); onRetried(r) },
    onError: (e) => setError(describeError(e)),
  })
  const g = grantLine(result.grant)
  const e = emailLine(result)
  const action = retryAction(result.grant?.outcome, result.notified)
  const retryBtn = action && (
    <span className="ml-2 inline-flex">
      <button type="button" className={btnSm} disabled={retry.isPending} onClick={() => retry.mutate()}>{retry.isPending ? 'Working…' : RETRY_LABEL[action]}</button>
    </span>
  )
  return (
    <div className="flex flex-col gap-3">
      {!result.created && result.emailed === false && action === null && <p className="m-0 text-[13px] text-paper-dim">This ride already had this award. Nothing was granted or sent again.</p>}
      <ol className="m-0 flex list-decimal flex-col gap-1.5 pl-5 text-[14px]">
        <li>★ Badge on the ride page and the hub: {result.honor}</li>
        <li>
          <span className={toneText[g.tone]}>{g.text}</span>
          {action === 'retry' && retryBtn}
          {error && <span role="alert" className="mt-1 block text-[12px] text-radar-red">{error}</span>}
        </li>
        <li>
          <span className={toneText[e.tone]}>{e.text}</span>
          {action === 'send' && retryBtn}
        </li>
        <li><a className="font-semibold text-amber-glow underline underline-offset-2" href={magazinePackageHref(pub, rideId)} download>Download the magazine package</a> (photos, story, build sheet, builder details)</li>
      </ol>
      <h3 className="m-0 mt-1 font-serif text-[18px] font-medium">Social posts</h3>
      <SocialCopy social={result.social} />
    </div>
  )
}

/** Award and notify, then show what happened. */
export function AwardPanel({ pub, ride, onDone, onCancel }: {
  pub: string; ride: RideDetail; onDone: () => void; onCancel: () => void
}) {
  const [kind, setKind] = useState<AwardKind>('rotm')
  const [period, setPeriod] = useState(() => defaultPeriod('rotm', new Date()))
  const [result, setResult] = useState<AwardResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const go = useMutation({
    mutationFn: () => awardRide(pub, ride.id, kind, period),
    onSuccess: (r) => { setError(null); setResult(r); onDone() },
    onError: (e) => setError(describeError(e)),
  })
  const pick = (k: AwardKind) => { setKind(k); setPeriod(defaultPeriod(k, new Date(), ride.challenge?.slug)); setError(null) }
  const label = awardLabel(kind, period, ride.challenge?.theme)
  const years = prizeYears(kind)

  return (
    <div role="group" aria-label="Award" className="flex flex-col gap-3 rounded-xl border border-amber-glow/60 bg-ink-700 p-4">
      {result ? (
        <>
          <strong>Awarded {result.honor} → {ride.title}</strong>
          <AwardOutcome pub={pub} rideId={ride.id} result={result} onRetried={(r) => { setResult(r); onDone() }} />
          <button type="button" className={`${btn} w-fit`} onClick={onCancel}>Done</button>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <label className={lab}>Award
              <select className={field} value={kind} onChange={(e) => pick(e.target.value as AwardKind)}>
                <option value="rotm">Ride of the Month</option>
                {ride.challenge && <option value="challenge">{ride.challenge.theme} challenge winner</option>}
                <option value="roty">Ride of the Year</option>
              </select>
            </label>
            {kind === 'rotm' && (
              <label className={lab}>Month
                <input type="month" className={field} value={period} onChange={(e) => setPeriod(e.target.value)} />
              </label>
            )}
            {kind === 'roty' && (
              <label className={lab}>Year
                <input className={`${field} w-28`} inputMode="numeric" maxLength={4} value={period} onChange={(e) => setPeriod(e.target.value.replace(/\D/g, ''))} />
              </label>
            )}
          </div>
          <strong>Award {label} → {ride.title}</strong>
          <ol className="m-0 flex list-decimal flex-col gap-1 pl-5 text-[14px] text-paper-dim">
            <li>Adds the ★ badge on the ride page and the hub</li>
            <li>Gives {ride.entrant.name} a {years}-year membership in AAMCirc (no charge), or adds {years === 1 ? 'a year' : `${years} years`} to the one they have</li>
            <li>Sends the winner email with the share kit</li>
            <li>Makes the magazine package for the print team, and the social posts</li>
          </ol>
          {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}
          <div className="flex gap-2">
            <button type="button" className={btnPrimary} disabled={go.isPending || !period} onClick={() => go.mutate()}>{go.isPending ? 'Awarding…' : 'Award and notify'}</button>
            <button type="button" className={btn} disabled={go.isPending} onClick={onCancel}>Cancel</button>
          </div>
        </>
      )}
    </div>
  )
}
